import { UserListingProps } from "../../utils/interface/userListing";
import { getSafeImageSrc } from "../../utils/util";
import { User, Users } from "lucide-react";
import defaultProfileImage from "../../assets/profile-img.svg";

interface UserListingExtendedProps extends UserListingProps {
  onEditUser: (user: UserListingProps['userList'][0]) => void;
  onDeleteClick?: (user: UserListingProps['userList'][0]) => void;
  loading?: boolean;
  onDeleteUser: (id: string, email: string) => void;
}

const MAX_DISPLAY_LENGTH = 15;

function truncateWithEllipsis(str: string, maxLen: number = MAX_DISPLAY_LENGTH): string {
  if (!str || str.length <= maxLen) return str;
  return str.slice(0, maxLen).trim() + "...";
}

const UserListing = ({ userList, onDeleteUser, onEditUser, onDeleteClick, loading = false }: UserListingExtendedProps) => {
  const handleEdit = (user: UserListingProps['userList'][0]) => {
    if (onEditUser) {
      onEditUser(user);
    }
  };

  if (loading) {
    return (
      <div className="py-24 flex flex-col items-center justify-center min-h-[280px]">
        <div className="relative">
          <div className="w-20 h-20 border-[3px] border-[#E761A0]/20 border-t-[#E761A0] rounded-full animate-spin" />
          <Users className="w-8 h-8 text-[#E761A0] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
        </div>
        <p className="text-white/90 text-base mt-5 font-medium">
          Loading users...
        </p>
        <p className="text-gray-500 text-sm mt-1">
          Fetching your user list
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {userList.length === 0 ? (
        <div className="text-center py-20 px-6">
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-2xl border border-[#E761A0]/30 bg-[#E761A0]/5 mb-6">
            <User className="w-12 h-12 text-[#E761A0]" />
          </div>
          <h3 className="text-white text-lg font-semibold mb-2">
            No users yet
          </h3>
          <p className="text-gray-400 text-sm max-w-sm mx-auto">
            Create your first user to get started and manage your team.
          </p>
        </div>
      ) : (
        <div className="grid  grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 w-full gap-6 sm:gap-8 lg:gap-x-12 lg:gap-y-10 xl:gap-x-[118px] xl:gap-y-[43px] mb-10">
          {userList.map((user, index) => (
            <div
              key={user.id || user.fullName}
              className="rounded-xl transition-all duration-200 hover:border-purple-500/30 flex flex-col items-center sm:items-start h-full"
            >
              <div className="p-0 flex flex-col w-full max-w-[280px] sm:max-w-none h-full">
                <div className="flex flex-col items-center sm:items-start mb-3 sm:mb-[14px]">
                  <div className="relative w-full max-w-[96px] sm:max-w-[128px] md:max-w-[144px] lg:max-w-[176px] xl:max-w-[210px] aspect-square rounded-full overflow-hidden flex-shrink-0 mb-4 sm:mb-6 lg:mb-8 xl:mb-[43px] bg-slate-700/30">
                    <img
                      src={getSafeImageSrc(user.profileImagePreview ?? user.profileImage, defaultProfileImage) || undefined}
                      alt={user.fullName?.trim() || user.email || "User"}
                      className="relative z-10 w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = defaultProfileImage;
                      }}
                    />
                  </div>
                  <h3 className="font-semibold text-lg sm:text-xl xl:text-[22px] text-center sm:text-left text-white line-clamp-2" title={user.fullName?.trim() || user.email || undefined}>
                    {truncateWithEllipsis(user.fullName?.trim() || user.email || "—")}
                  </h3>
                </div>
                <div className="mb-4 text-center sm:text-left">
                  {/* <p className="text-white text-lg truncate font-light" title={user.email}>{user.email}</p> */}
                  <span className="text-sm sm:text-lg font-light text-white block truncate">
                    {user.jobTitle?.trim() || " "}
                  </span>
                  <p
                    className="text-sm sm:text-lg font-light text-white truncate"
                    title={user.companyName?.trim() || undefined}
                  >
                    {user.companyName?.trim() || " "}
                  </p>

                </div>
                <div className="flex items-center gap-2 w-full justify-center sm:justify-start mt-auto">
                  <button
                    type="button"
                    className="group flex items-center justify-center gap-1.5 sm:gap-2 
             rounded-[27px] border-[2px] border-[#65449B] 
             bg-[#65449B] text-white 
             px-4 py-2.5 sm:px-6 sm:py-[11px] 
             text-sm sm:text-[16px] font-semibold tracking-wide 
             transition-all duration-300 ease-out 
             hover:bg-transparent hover:shadow-none 
             hover:border-[#65449B] 
               sm:w-auto sm:flex-1 sm:max-w-[148px] button
xl:min-h-[54px] min-h-[45px] text-[12px] xl:text-[16px]
 "
                    onClick={() => handleEdit(user)}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="26.997"
                      height="26.953"
                      viewBox="0 0 26.997 26.953"
                      fill="currentColor"
                      className="text-white 
               transform transition-transform duration-500 ease-out 
               group-hover:rotate-12 
               group-hover:text-purple-400"
                    >
                      <path d="M22.392,16.076a1.4,1.4,0,0,1,2.8,0v7.2a3.685,3.685,0,0,1-3.674,3.674H3.674A3.685,3.685,0,0,1,0,23.279V5.318A3.685,3.685,0,0,1,3.674,1.644h7.678a1.4,1.4,0,0,1,0,2.8H3.674a.877.877,0,0,0-.875.875V23.279a.877.877,0,0,0,.875.875H21.516a.877.877,0,0,0,.875-.875ZM23.206.332l3.46,3.46a1.141,1.141,0,0,1,0,1.608L15.27,16.8l-5.854.827.765-5.874L21.6.332a1.141,1.141,0,0,1,1.608,0" />
                    </svg>

                    <span className="uppercase tracking-wide">EDIT</span>
                  </button>


                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UserListing
